import { useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, ListChecks, Loader2, LogOut, Settings } from "lucide-react";
import { WordMark } from "@/components/ui/WordMark";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { AdminAuthProvider, useAdminAuth } from "@/lib/admin-auth";
import { AdminLogin } from "@/routes/AdminLogin";
import { cn } from "@/lib/cn";

const links = [
  { to: "/admin", label: "Events", icon: ListChecks, end: true },
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export function AdminLayout() {
  return (
    <AdminAuthProvider>
      <AdminGate />
    </AdminAuthProvider>
  );
}

function AdminGate() {
  const { status, logout } = useAdminAuth();

  useEffect(() => {
    // No-op: presence keeps the auth check alive at mount.
  }, []);

  if (status === "loading") {
    return (
      <div className="grid min-h-dvh place-items-center text-muted">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (status !== "authed") {
    return <AdminLogin />;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas lg:grid lg:grid-cols-[260px,1fr]">
      <aside className="border-b border-line/70 bg-surface/70 backdrop-blur lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-6 py-5">
          <WordMark />
          <ThemeToggle />
        </div>
        <nav className="flex gap-1 overflow-x-auto px-4 pb-4 lg:flex-col lg:px-4 lg:pb-6">
          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "inline-flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors lg:w-full",
                  isActive
                    ? "bg-accent-soft text-accent-ink dark:text-accent"
                    : "text-muted hover:bg-ink/5 hover:text-ink dark:hover:bg-white/5",
                )
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block px-4 py-6">
          <button
            type="button"
            onClick={() => void logout()}
            className="inline-flex w-full items-center gap-2 rounded-xl border border-line/70 px-3 py-2.5 text-sm text-muted hover:border-accent hover:text-accent transition-colors"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 px-5 py-8 sm:px-8 lg:px-12 lg:py-12">
        <Outlet />
      </main>
    </div>
  );
}
